import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Check, ThumbsUp, ThumbsDown, ExternalLink, Link2, Volume2, Square, FileText, Loader2 } from "lucide-react";
import { BriefDialog } from "@/components/chat/BriefDialog";
import { speech, spokenText } from "@/lib/speech";
import { useSpeech } from "@/components/chat/SpeakingBar";
import { FundCard } from "@/components/chat/FundCard";
import { CompareTable, FundList } from "@/components/chat/CompareTable";
import { t } from "@/lib/i18n";

const numbered = (sources) => [...(sources || [])].sort((a, b) => a.id - b.id).map((s, i) => ({ ...s, n: i + 1 }));

const withCitations = (text, sources) => {
  const map = Object.fromEntries(numbered(sources).map((s) => [s.id, s]));
  return text.replace(/\[(\d+)\](?!\()/g, (m, n) => (map[n] ? `[\\[${map[n].n}\\]](${map[n].url} "cite")` : m));
};

const Md = ({ text, sources }) => (
  <div className="hv-md text-[14px] leading-relaxed text-hv-text">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
      a: ({ href, title, children }) => <a href={href} target="_blank" rel="noreferrer" className={title === "cite" ? "hv-cite" : undefined}>{children}</a>,
    }}>{withCitations(text, sources)}</ReactMarkdown>
  </div>
);

const Component = ({ c, lang }) => {
  if (c.type === "fund_card") return <FundCard card={c} lang={lang} />;
  if (c.type === "compare_table") return <CompareTable data={c} lang={lang} />;
  if (c.type === "fund_list") return <FundList data={c} lang={lang} />;
  return null;
};

export const SourceChips = ({ sources, lang }) => (
  <div className="mt-3" data-testid="source-chips">
    <p className="font-heading text-[10px] font-semibold uppercase tracking-wider text-hv-muted">{t(lang).sources}</p>
    <ul className="mt-1.5 flex flex-wrap gap-1.5">
      {numbered(sources).map((s) => (
        <li key={s.id}>
          <a href={s.url} target="_blank" rel="noreferrer" data-testid={`source-chip-${s.id}`}
            className="inline-flex max-w-[330px] items-center gap-1.5 border border-hv-rule bg-white px-2 py-1 text-[11px] text-hv-logo-navy transition-colors hover:border-hv-navy">
            <span className="font-heading font-bold">{s.n}</span>
            <span className="truncate">{s.title}</span>
            {s.date && <span className="whitespace-nowrap text-hv-muted">· {s.date}</span>}
            <ExternalLink size={11} aria-hidden="true" />
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const ListenButton = ({ msg, lang, s, btn }) => {
  const st = useSpeech();
  const mine = st.speakingId === msg.id;
  const toggle = () => (mine ? speech.stop() : speech.speak(msg.id, spokenText(msg.text, lang)));
  return (
    <button type="button" className={`${btn} ${mine ? "text-hv-navy" : ""}`} onClick={toggle} aria-pressed={mine}
      aria-label={mine ? s.stopListening : s.listen} title={mine ? s.stopListening : s.listen} data-testid="message-listen-button">
      {mine && st.loading ? <Loader2 size={14} className="animate-spin" /> : mine ? <Square size={13} /> : <Volume2 size={14} />}
    </button>
  );
};

const Actions = ({ msg, lang, onFeedback }) => {
  const s = t(lang);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard?.writeText(msg.text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const [shared, setShared] = useState(false);
  const share = async () => {
    const url = `${window.location.origin}/share/${msg.messageId}`;
    if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
      await navigator.share({ title: "Ceres · Harvest ETFs", url }).catch(() => {});
      return;
    }
    await navigator.clipboard?.writeText(url).catch(() => {});
    setShared(true);
    setTimeout(() => setShared(false), 2000);
  };
  const btn = "inline-flex h-7 w-7 items-center justify-center text-hv-muted transition-colors hover:bg-hv-section hover:text-hv-navy";
  return (
    <div className="mt-2 flex items-center gap-1">
      <button type="button" className={btn} onClick={copy} aria-label={copied ? s.copied : s.copy} data-testid="message-copy-button">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
      <button type="button" className={`${btn} ${msg.feedback === "up" ? "text-hv-navy" : ""}`} aria-pressed={msg.feedback === "up"} aria-label={s.up} onClick={() => onFeedback(msg, "up")} data-testid="message-thumbs-up-button"><ThumbsUp size={14} fill={msg.feedback === "up" ? "currentColor" : "none"} /></button>
      <button type="button" className={`${btn} ${msg.feedback === "down" ? "text-hv-navy" : ""}`} aria-pressed={msg.feedback === "down"} aria-label={s.down} onClick={() => onFeedback(msg, "down")} data-testid="message-thumbs-down-button"><ThumbsDown size={14} fill={msg.feedback === "down" ? "currentColor" : "none"} /></button>
      <ListenButton msg={msg} lang={lang} s={s} btn={btn} />
      {msg.messageId && (
        <BriefDialog messageId={msg.messageId} trigger={
          <button type="button" className={btn} aria-label={s.brief} title={s.brief} data-testid="message-brief-button"><FileText size={14} /></button>
        } />
      )}
      {msg.messageId && (
        <button type="button" className={btn} onClick={share} aria-label={s.share} title={s.share} data-testid="message-share-button"><Link2 size={14} /></button>
      )}
      {shared && <span className="text-[11px] text-hv-navy" role="status" data-testid="message-share-confirmation">{s.shared}</span>}
      {msg.feedback && <span className="text-[11px] text-hv-muted" role="status">{s.thanks}</span>}
    </div>
  );
};

const Thinking = ({ lang }) => (
  <p className="flex items-center gap-1 text-[13px] text-hv-muted" role="status" data-testid="assistant-thinking">
    {t(lang).thinking}
    {[0, 1, 2].map((i) => <span key={i} className="hv-dot" style={{ animationDelay: `${i * 0.2}s` }}>.</span>)}
  </p>
);

export const Message = ({ msg, lang, onFeedback, readOnly = false }) => {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid="user-message">
        <p className="max-w-[85%] whitespace-pre-wrap bg-hv-navy px-3.5 py-2.5 text-[14px] text-white">{msg.text}</p>
      </div>
    );
  }
  const [lead, ...rest] = msg.text.split(/\n\n/);
  const hasComp = msg.components?.length > 0;
  return (
    <div className="hv-rise space-y-3" data-testid="assistant-message">
      {msg.text ? <Md text={hasComp ? lead : msg.text} sources={msg.sources} /> : msg.pending && <Thinking lang={lang} />}
      {msg.components?.map((c, i) => <Component key={i} c={c} lang={lang} />)}
      {hasComp && rest.length > 0 && <Md text={rest.join("\n\n")} sources={msg.sources} />}
      {msg.pending && msg.text && <Thinking lang={lang} />}
      {!msg.pending && msg.sources?.length > 0 && <SourceChips sources={msg.sources} lang={lang} />}
      {!readOnly && !msg.local && !msg.interrupted && !msg.pending && msg.text && !msg.error && <Actions msg={msg} lang={lang} onFeedback={onFeedback} />}
    </div>
  );
};
